import React from 'react';
import { Boxes } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { getActiveRestaurantId } from '../../utils/roleAccess';
import { EmptyState } from '../../components/LoadingStates';

/**
 * Shared shell for every inventory sub-page. Renders the page header and
 * resolves the org + active restaurant scope, showing a guard state when
 * either is missing. The child render-prop only runs once scope is available.
 */
export const InventoryPageShell: React.FC<{
  title: string;
  description?: string;
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  children: (ctx: { org: string; restaurantId: string }) => React.ReactNode;
}> = ({ title, description, icon: Icon, children }) => {
  const { user } = useAuth();
  const { currentTheme } = useTheme();
  const org = user?.organizationCode;
  const restaurantId = getActiveRestaurantId() || undefined;

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center gap-3.5 mb-6">
        <div className="grid place-items-center h-12 w-12 rounded-2xl text-white shrink-0" style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary[500]}, ${currentTheme.colors.primary[700]})`, boxShadow: `0 14px 32px -12px ${currentTheme.colors.primary[500]}` }}>
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight" style={{ color: currentTheme.colors.text.primary }}>{title}</h1>
          {description && <p style={{ color: currentTheme.colors.text.secondary }}>{description}</p>}
        </div>
      </div>

      {!org ? (
        <EmptyState
          icon={<Boxes className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />}
          title="No organization selected"
          description="Your account is not linked to an organization."
        />
      ) : !restaurantId ? (
        <EmptyState
          icon={<Boxes className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />}
          title="Select a restaurant"
          description="Inventory is tracked per restaurant. Pick a restaurant from the profile menu to manage its stock."
        />
      ) : (
        children({ org, restaurantId })
      )}
    </div>
  );
};
